// ============================================================================
// Hooks TanStack Query — ADMINISTRATION (AD-E) : onglet Contrats (referentiel)
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (P-ADM-2, routes/admin.ts : canAccessGestAdmin = admin SEUL — fonctions repo
// ADMIN-WIDE, jamais le grant par cible). Le bundle contract-templates est charge
// par onglet (P-ADM-8) sous ADMIN_ROOT_KEY. Les mutations invalident la famille
// admin (['gest','admin']).
//
// [!] PERIMETRE AD-E : ce onglet gere le CATALOGUE de gabarits (nom + corps a
// jetons de fusion), PAS l'emission d'un contrat a un employe (celle-ci vit cote
// fiche employe). M-163 : supprimer un gabarit n'affecte AUCUN contrat deja emis
// (rendered_body fige a l'emission — le referentiel est une source d'amorcage).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY, type GestAdminRole } from './useAdminAudit';

// ─── Types — MIROIR EXACT des DTOs serveur (routes/admin.ts AD-E) ─────────────

/** Ligne du catalogue (miroir AdminContractTemplateDto) — nom + corps a jetons. */
export interface AdminContractTemplate {
  id: string;
  name: string;
  body: string;
}

export interface AdminContractTemplatesResponse {
  viewer: { role: GestAdminRole; email: string };
  templates: AdminContractTemplate[];
}

export interface ContractTemplatePayload {
  name: string;
  body: string;
}

// ─── Codes d'erreur metier (GA-21 — pattern adminMemberErrorCode) ─────────────

/**
 * Extrait le code metier VERBATIM renvoye par le serveur ({ code }), sinon un
 * code generique par statut. Mappe sur gest:admin.contracts.errors.<code>.
 * Codes serveur connus : invalid (Zod), notfound (404), forbidden (403).
 */
export function adminContractErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (typeof code === 'string' && code.length > 0 && !code.startsWith('GEST_')) return code;
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Query (bundle par onglet, P-ADM-8) ───────────────────────────────────────

const adminContractsKey = [...ADMIN_ROOT_KEY, 'contract-templates'] as const;

/** AD-E : bundle de l'onglet CONTRATS (liste + corps de chaque gabarit). */
export function useAdminContractTemplates(enabled: boolean) {
  return useQuery({
    queryKey: adminContractsKey,
    queryFn: () => api.get<AdminContractTemplatesResponse>('/api/admin/contract-templates'),
    enabled,
    retry: false,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

function useInvalidateContracts() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY });
  };
}

/** AD-E : creation d'un gabarit (name + body). */
export function useCreateContractTemplate() {
  const invalidate = useInvalidateContracts();
  return useMutation({
    mutationFn: (payload: ContractTemplatePayload) =>
      api.post<{ id: string }>('/api/admin/contract-templates', payload),
    onSuccess: () => invalidate(),
  });
}

/** AD-E : edition d'un gabarit (name + body). */
export function useUpdateContractTemplate() {
  const invalidate = useInvalidateContracts();
  return useMutation({
    mutationFn: (input: { id: string; payload: ContractTemplatePayload }) =>
      api.put<{ ok: boolean }>(
        `/api/admin/contract-templates/${encodeURIComponent(input.id)}`,
        input.payload,
      ),
    onSuccess: () => invalidate(),
  });
}

/** AD-E : soft-delete d'un gabarit (M-163 : les contrats emis restent intacts). */
export function useDeleteContractTemplate() {
  const invalidate = useInvalidateContracts();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      api.delete<{ ok: boolean }>(`/api/admin/contract-templates/${encodeURIComponent(input.id)}`),
    onSuccess: () => invalidate(),
  });
}
